import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAssetConfig } from "../../../../Api/AssetApi";
import CODE from "../../../../Static/Constants/StatusCodes";

// The two halves of the Assets tab's configuration, in one hook because every
// caller needs both and they are read at the same moment.
//
// 1. HOW THE LIST RENDERS -- metaInfo.table.assetsTable, the merged
//    pageMeta/orgMeta payload. Synchronous, already in hand.
//
//    `metaInfo` MUST be usePageMeta's `pagesContent.pageMeta`, not
//    `pagesContent` itself: the hook returns a hand-built wrapper and the
//    payload sits one level down, so passing the wrapper makes every lookup
//    here undefined and the tab renders "not configured" forever. Same trap as
//    useEntityTypeConfig.
//
// 2. WHAT THE STATES ARE -- GET /api/assets/config. Asynchronous, because the
//    lifecycle states are a TABLE (assetState) and not meta: five columns
//    across the module carry real foreign keys to assetState.code, and you
//    cannot foreign-key to a JSON blob. A second copy in orgMeta could only
//    disagree with the table the constraints point at.
//
// The endpoint is guarded by ASSETS:view alone, so anyone who can open this
// tab can always load it.

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

const RETIRED_COLUMNS = new Set(["receiptCount"]);

export const useAssetConfig = (metaInfo) => {
    const tableConfig = useMemo(
        () => metaInfo?.table?.assetsTable || null,
        [metaInfo]
    );

    const columnsConfig = useMemo(
        () => tableConfig?.columnsConfig || EMPTY_OBJECT,
        [tableConfig]
    );

    // COLUMNS THE PRODUCT HAS RETIRED, dropped here whatever pageMeta says.
    //
    // pageMeta is a database row per environment; the seeder only feeds NEW
    // ones. So removing a column from the seeded JSON does nothing to an org
    // that already has a row, and until somebody runs the update by hand the
    // table renders a header over a column of dashes -- and the CSV carries
    // the header with blank cells.
    //
    // receiptCount also still declares "sorter": true in those stale rows, so
    // clicking the header sends sortBy=receiptCount and the API now answers
    // 400 NOT_SORTABLE: the list ERRORS rather than degrading. This list is
    // the code-side defence, so the DDL step is a tidy-up rather than a fix.
    const columnOrdering = useMemo(
        () => (tableConfig?.columnOrdering || EMPTY_ARRAY)
            .filter((key) => !RETIRED_COLUMNS.has(key)),
        [tableConfig]
    );

    const searchableFields = useMemo(
        () => tableConfig?.searchableFields || EMPTY_ARRAY,
        [tableConfig]
    );

    const [assetStates, setAssetStates] = useState(EMPTY_ARRAY);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    // Bumped by refetch; the effect keys off it so a retry does not need a
    // second code path.
    const [refresh, setRefresh] = useState(0);
    const requestSeq = useRef(0);

    useEffect(() => {
        const seq = ++requestSeq.current;
        setIsLoading(true);
        (async () => {
            const res = await getAssetConfig();
            if (seq !== requestSeq.current) return;

            if (res?.responseStatus?.code === CODE.SUCCESS) {
                const states = res?.data?.assetStates;
                setAssetStates(Array.isArray(states) ? states : EMPTY_ARRAY);
                setErrorMessage("");
            } else {
                // An empty list is a legitimate answer -- an org that has not
                // seeded its states yet -- so a FAILURE must not look like
                // one. The badges are suppressed and the reason is shown.
                setAssetStates(EMPTY_ARRAY);
                setErrorMessage(
                    res?.responseStatus?.msg || "Could not load the asset states"
                );
            }
            setIsLoading(false);
        })();
    }, [refresh]);

    const refetch = useCallback(() => setRefresh((n) => n + 1), []);

    // Code -> state, for resolving a label and a badge theme without a scan
    // per row. Includes retired states only if the API still returns them, so
    // a caller must tolerate a miss: an asset can sit in a state the org has
    // since removed from the table.
    const statesByCode = useMemo(
        () =>
            Object.fromEntries(
                assetStates
                    .filter((state) => state?.code)
                    .map((state) => [state.code, state])
            ),
        [assetStates]
    );

    return {
        tableConfig,
        columnsConfig,
        columnOrdering,
        searchableFields,
        assetStates,
        statesByCode,
        isLoading,
        errorMessage,
        refetch,
        // Only meaningful once the caller has confirmed the payload arrived.
        // AssetManagement.jsx gates on pagesContent.mounted before rendering
        // this tab, so reaching here without columns really does mean the
        // table is not configured for the org.
        isConfigured: columnOrdering.length > 0,
    };
};

export default useAssetConfig;
